import React, { useContext } from 'react';
import { Box, IconButton, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { CardHeader, CardSection } from '@europrocurement/flexy-components';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { Link } from 'react-router-dom';
import {
    DossierCompanyFormality,
    DossierDateCreation,
    DossierNotifications,
    DossierReferences,
    DossierRoutingContext,
    DossierStatus,
    DossierStepCompletion,
} from '@europrocurement/l2d-modules/modules/Dossiers';
import { DossierOpenSearch, NestedDossierFormaliteOpenSearch } from '@europrocurement/l2d-domain';

type DossiersCollectionProps = {
    collectionNumber: string;
    dossiers: DossierOpenSearch[];
};

const DossiersCollection: React.FunctionComponent<DossiersCollectionProps> = function ({
    collectionNumber,
    dossiers,
}) {
    const { getDossierRoute } = useContext(DossierRoutingContext);

    if (!dossiers || dossiers.length === 0) return null;

    const titleSx = {
        fontSize: '20px',
        lineHeight: '28px',
    };
    const headingSx = {
        borderLeftWidth: '2px',
        minHeight: 'auto',
    };

    const linkStyle = {
        borderRadius: '5px',
        display: 'flex',
        margin: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
        width: '40px',
        height: '40px',
        '&:hover': {
            backgroundColor: '#11111111',
        },
    };
    const linkIconStyle = {
        fontSize: '16px',
    };

    const cardHeader = (
        <Box>
            <CardHeader
                title={`Formalités de la commande #${collectionNumber}`}
                titleSx={titleSx}
                sx={headingSx}
            />
        </Box>
    );

    const items = dossiers.map((dossier, index) => {
        const cellSx = index < dossiers.length - 1 ? {} : { border: 'none' };

        return (
            <TableRow key={dossier.numero}>
                <TableCell sx={cellSx}>
                    <DossierReferences
                        itemReference={dossier.numero as string}
                        companyReference={dossier.societeRef as string}
                        direction="column"
                    />
                </TableCell>
                <TableCell sx={cellSx}>
                    <DossierCompanyFormality
                        libelle={dossier.clientOpereLibelle as string}
                        formalites={dossier.formalites?.map(
                            (formalite) =>
                                ({
                                    id: formalite?.id as number,
                                    libelle: formalite?.libelle as string,
                                }) as NestedDossierFormaliteOpenSearch,
                        )}
                    />
                </TableCell>
                <TableCell sx={cellSx}>
                    <DossierStepCompletion
                        step={dossier.stepLabel as string}
                        currentCompletion={dossier.stepCompletion as number}
                    />
                </TableCell>
                <TableCell sx={cellSx}>
                    {dossier.dateCreation && (
                        <DossierDateCreation date={new Date(dossier.dateCreation)} />
                    )}
                </TableCell>
                <TableCell sx={cellSx}>
                    <DossierNotifications documentsCount={dossier.documentsCount as number} />
                </TableCell>
                <TableCell sx={cellSx}>
                    <DossierStatus
                        statutId={dossier.statutId as number}
                        label={dossier.statutLibelle as string}
                    />
                </TableCell>
                <TableCell sx={cellSx}>
                    <Box>
                        <Link
                            to={getDossierRoute(dossier.id as number)}
                            style={{ textDecoration: 'none' }}
                        >
                            <IconButton sx={linkStyle}>
                                <FaOptionIcon
                                    icon={['fasl', 'chevron-right']}
                                    sx={linkIconStyle}
                                    size="xs"
                                />
                            </IconButton>
                        </Link>
                    </Box>
                </TableCell>
            </TableRow>
        );
    });

    return (
        <CardSection
            header={cardHeader}
            sxContent={{ padding: '24px 32px' }}
        >
            <Table>
                <TableBody>{items}</TableBody>
            </Table>
        </CardSection>
    );
};

export default DossiersCollection;
